// =============================================================================
// InsuranceScreen — Commercial > Insurance (mig 403, 29 Sep 2026)
// =============================================================================
// The third cost bucket. James, 27–29 Sep: "vehicles, loans and insurance …
// buckets we should cleanly see." Same shape as Vehicles (397): the Xero
// account on the bill is the breakdown, the account list lives in Finance
// settings (insurance_xero_accounts), v_insurance_spend does the grouping —
// plus the thing insurance needs: WHICH POLICY. The policies are the company
// insurance program (company_compliance_items, mig 353); a bill points at one
// from the coding card.
//
// And the BK-313 rule this board makes visible: a premium Elantis pays is
// SETTLED BY THE LOAN — it shows here as a cost, but nothing leaves the bank
// for it; the Elantis instalments (Commercial > Loans) do that. Director only,
// like Loans.
// =============================================================================
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import { ShieldCheck, ArrowLeft, CalendarDays, Receipt, Wallet, Info, Landmark, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, KPI, Pill, Button, Spinner, EmptyState } from "@/components/ui";
import { fmtMoney, fmtDate } from "@/lib/dates";

const COLOURS: Record<string, string> = {
  "6020": "#3B82F6",   // public liability
  "6021": "#10B981",   // workcover
  "6022": "#F97316",   // vehicles
  uncoded: "#6B7280",
};
const FALLBACK_COLOURS = ["#A855F7", "#EAB308", "#EC4899", "#14B8A6", "#EF4444"];
const NO_POLICY = "__none__";

function dayKey(d: Date) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function fyEnding(now = new Date()) { return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear(); }
function fyLabel(ending: number) { return `FY${String(ending - 1).slice(2)}/${String(ending).slice(2)}`; }

interface CostLine { xero_account_code: string; xero_account_name: string; account_type: string | null; in_chart: boolean; in_map: boolean; is_running_cost: boolean; sort_order: number }
const UNCODED: CostLine = { xero_account_code: "uncoded", xero_account_name: "No Xero account on the bill", account_type: null, in_chart: false, in_map: false, is_running_cost: true, sort_order: 9999 };

export default function InsuranceScreen() {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const canSee = hasRole(user, "director");   // James, 28 Sep: loans and insurance director-only
  const [savingBill, setSavingBill] = useState<string | null>(null);
  const [lineFilter, setLineFilter] = useState<string>("all");
  const [policyFilter, setPolicyFilter] = useState<string>("all");

  const today = new Date();
  const thisFy = fyEnding(today);
  const lastFy = thisFy - 1;
  const from36 = dayKey(new Date(today.getFullYear() - 3, today.getMonth(), 1));
  const in90 = dayKey(new Date(today.getTime() + 90 * 86_400_000));
  const todayK = dayKey(today);

  const { data: lines = [] } = useQuery({
    queryKey: ["insurance_accounts"], enabled: canSee,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("v_insurance_accounts").select("*").order("sort_order");
      if (error) throw error; return (data ?? []) as CostLine[];
    },
  });
  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ["insurance_spend", from36], enabled: canSee,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("v_insurance_spend").select("*").gte("spend_date", from36).order("spend_date", { ascending: false }).limit(3000);
      if (error) throw error; return (data ?? []) as any[];
    },
  });
  // The program itself — every active insurance item, whether or not a bill
  // has named it yet. Renewals come from here.
  const { data: policies = [] } = useQuery({
    queryKey: ["insurance_policies_for_board"], enabled: canSee,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("company_compliance_items")
        .select("item_id, item_type, category, display_name, provider, broker, reference, cover_amount, premium_estimate, effective_from, expiry_date, is_active")
        .eq("item_type", "insurance")
        .eq("is_active", true)
        .order("expiry_date", { ascending: true, nullsFirst: false });
      if (error) throw error; return (data ?? []) as any[];
    },
  });
  // Premium-funding loans, so a policy row can say "funded by Elantis".
  const { data: fundingLinks = [] } = useQuery({
    queryKey: ["insurance_funding_links"], enabled: canSee,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("loan_links")
        .select("compliance_item_id, loan:loan_id (loan_id, lender, title, status, facility_type)")
        .not("compliance_item_id", "is", null);
      if (error) throw error; return (data ?? []) as any[];
    },
  });

  const allLines = useMemo<CostLine[]>(() => {
    const out = [...lines];
    if (rows.some(r => r.cost_line === "uncoded") && !out.some(l => l.xero_account_code === "uncoded")) out.push(UNCODED);
    return out;
  }, [lines, rows]);
  const colourFor = (code: string, i: number) => COLOURS[code] ?? FALLBACK_COLOURS[i % FALLBACK_COLOURS.length];
  const nameFor = useMemo(() => { const m = new Map(allLines.map(l => [l.xero_account_code, l.xero_account_name])); return (c: string) => m.get(c) ?? (c === "uncoded" ? UNCODED.xero_account_name : `Xero ${c}`); }, [allLines]);
  const money0 = (n: number) => "$" + Math.round(n).toLocaleString("en-AU");
  const fundedBy = useMemo(() => {
    const m = new Map<string, any>();
    for (const l of fundingLinks) if (l.compliance_item_id && l.loan && l.loan.status !== "closed") m.set(l.compliance_item_id, l.loan);
    return m;
  }, [fundingLinks]);

  const totals = useMemo(() => {
    let fy = 0, last = 0, byLoan = 0, unassigned = 0;
    for (const r of rows) {
      const v = Number(r.amount_ex_gst ?? 0);
      if (r.fy_ending === thisFy) { fy += v; if (r.settled_by === "loan") byLoan += v; if (!r.policy_item_id) unassigned += v; }
      if (r.fy_ending === lastFy) last += v;
    }
    return { fy, last, byLoan, unassigned };
  }, [rows, thisFy, lastFy]);

  const byLine = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of allLines) m.set(l.xero_account_code, 0);
    for (const r of rows) { if (r.fy_ending !== thisFy) continue; const c = String(r.cost_line); m.set(c, (m.get(c) ?? 0) + Number(r.amount_ex_gst ?? 0)); }
    return [...m.entries()].map(([code, value]) => ({ code, name: nameFor(code), value })).sort((a, b) => b.value - a.value);
  }, [rows, allLines, thisFy, nameFor]);
  const byLineWithSpend = byLine.filter(c => c.value > 0);

  // -- per policy: the program, with this FY and last FY against each ---------
  const perPolicy = useMemo(() => {
    const m = new Map<string, { key: string; name: string; category: string | null; provider: string | null; reference: string | null; expiry: string | null; estimate: number | null; fy: number; last: number; count: number; settledByLoan: boolean; funded: any }>();
    for (const p of policies) m.set(p.item_id, { key: p.item_id, name: p.display_name, category: p.category, provider: p.provider, reference: p.reference, expiry: p.expiry_date, estimate: p.premium_estimate == null ? null : Number(p.premium_estimate), fy: 0, last: 0, count: 0, settledByLoan: false, funded: fundedBy.get(p.item_id) ?? null });
    m.set(NO_POLICY, { key: NO_POLICY, name: "Not against a policy", category: null, provider: null, reference: null, expiry: null, estimate: null, fy: 0, last: 0, count: 0, settledByLoan: false, funded: null });
    for (const r of rows) {
      const key = r.policy_item_id ?? NO_POLICY;
      const hit = m.get(key) ?? m.set(key, { key, name: r.policy_name ?? "Unknown policy", category: r.policy_category, provider: r.policy_provider, reference: r.policy_reference, expiry: r.policy_expiry, estimate: null, fy: 0, last: 0, count: 0, settledByLoan: false, funded: fundedBy.get(key) ?? null }).get(key)!;
      const v = Number(r.amount_ex_gst ?? 0);
      if (r.fy_ending === thisFy) { hit.fy += v; hit.count += 1; if (r.settled_by === "loan") hit.settledByLoan = true; }
      if (r.fy_ending === lastFy) hit.last += v;
    }
    return [...m.values()]
      .filter(x => x.key !== NO_POLICY || x.fy > 0 || x.last > 0)
      .sort((a, b) => (a.key === NO_POLICY ? 1 : b.key === NO_POLICY ? -1 : b.fy - a.fy || (a.expiry ?? "9999").localeCompare(b.expiry ?? "9999")));
  }, [rows, policies, thisFy, lastFy, fundedBy]);

  // James, 29 Sep (first look at the board): the WorkCover bill sat under "Not
  // against a policy" because nobody had picked the policy on the coding
  // card - it read as a bug. So: pick it HERE, on the bill row, and the board
  // suggests the obvious match (supplier name ~ the policy's provider/broker/
  // name) so it is one click, not a hunt.
  const norm = (x: string | null | undefined) => String(x ?? "").toLowerCase().replace(/pty|ltd|limited|australia|queensland|qld|\W/g, "");
  const suggestFor = (b: any): string | null => {
    const sup = norm(b.supplier_name);
    if (!sup) return null;
    const hit = policies.find((p: any) => {
      const cands = [p.provider, p.broker, p.display_name].map(norm).filter(Boolean);
      return cands.some(c => c.length >= 4 && (sup.includes(c) || c.includes(sup)));
    });
    return hit?.item_id ?? null;
  };
  async function setBillPolicy(billId: string, itemId: string) {
    setSavingBill(billId);
    const { error } = await (supabase as any).from("ap_invoices").update({ policy_item_id: itemId || null }).eq("ap_invoice_id", billId);
    setSavingBill(null);
    if (error) { window.alert(`Could not set the policy: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["insurance_spend"] });
  }

  const renewals = useMemo(() => policies
    .filter((p: any) => p.expiry_date && p.expiry_date <= in90)
    .map((p: any) => ({ ...p, overdue: p.expiry_date < todayK })), [policies, in90, todayK]);
  const noDate = policies.filter((p: any) => !p.expiry_date);

  const bills = useMemo(() => rows
    .filter(r => lineFilter === "all" || String(r.cost_line) === lineFilter)
    .filter(r => policyFilter === "all" || (policyFilter === NO_POLICY ? !r.policy_item_id : r.policy_item_id === policyFilter))
    .slice(0, 300), [rows, lineFilter, policyFilter]);
  const notInChart = allLines.filter(l => !l.in_chart && l.xero_account_code !== "uncoded");

  if (!canSee) {
    return <div className="p-6 max-w-md mx-auto"><Card><EmptyState title="Directors only" description="Insurance costs are restricted to directors." icon={ShieldCheck} /></Card></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto">
      <Link to="/commercial" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"><ArrowLeft size={14}/> Commercial dashboard</Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2"><ShieldCheck size={22} className="text-accent" /> Insurance</h1>
          <div className="text-text-muted text-[13px] mt-1">What cover costs — by policy and by the Xero account on each bill, financial year by financial year. A premium the funder pays shows here as a cost and on Loans as the instalments; it is never paid from the bank twice. Actuals from AP, ex-GST.</div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/company-compliance"><Button variant="secondary" size="sm" icon={ShieldCheck}>Insurance program</Button></Link>
          <Link to="/commercial/loans"><Button variant="secondary" size="sm" icon={Landmark}>Loans</Button></Link>
          <Link to="/ap"><Button variant="secondary" size="sm" icon={Wallet}>AP</Button></Link>
        </div>
      </div>

      {error && <div className="mb-4 bg-danger/10 border border-danger/30 rounded-md p-3 text-[12px] text-danger">Insurance spend failed to load: {(error as any)?.message ?? String(error)}</div>}
      {notInChart.length > 0 && (
        <div className="mb-4 bg-warn/10 border border-warn/30 rounded-md p-3 text-[12px] text-warn">
          {notInChart.map(l => l.xero_account_code).join(", ")} {notInChart.length === 1 ? "is" : "are"} not in Ops's copy of the Xero chart of accounts yet — showing our own name for it. Sync the chart from the Xero hand-off screen once the bookkeeper has added it.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI label={`${fyLabel(thisFy)} premiums`} value={fmtMoney(totals.fy)} sub="ex-GST, from 1 July" icon={CalendarDays} accent />
        <KPI label={`${fyLabel(lastFy)}`} value={fmtMoney(totals.last)} sub="last financial year, for the comparison" />
        <KPI label="Settled by a funder" value={fmtMoney(totals.byLoan)} sub={`${fyLabel(thisFy)} — Elantis pays the insurer, not the bank`} />
        <KPI label="Renewals in 90 days" value={String(renewals.length)} sub={renewals.some((r: any) => r.overdue) ? `${renewals.filter((r: any) => r.overdue).length} already past expiry` : "from the insurance program"} />
      </div>

      {isLoading ? <div className="py-10 flex justify-center"><Spinner/></div> : (
        <>
          {/* Renewals first — the thing that bites if missed */}
          {(renewals.length > 0 || noDate.length > 0) && (
            <Card className="mb-4">
              <div className="p-4 border-b border-line">
                <div className="font-semibold text-[14px] flex items-center gap-2"><AlertTriangle size={15} className="text-warn" /> Coming up for renewal</div>
                <div className="text-[11px] text-text-muted mt-0.5">Expiry dates from the insurance program. Renew on the Company compliance screen; the new premium bill gets the policy picked on the coding card.</div>
              </div>
              <div className="divide-y divide-line">
                {renewals.map((p: any) => (
                  <div key={p.item_id} className="px-4 py-2.5 flex items-center gap-3 text-[13px]">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${p.overdue ? "bg-danger" : "bg-warn"}`} />
                    <span className="flex-1 min-w-0 truncate">{p.display_name}<span className="text-text-muted"> · {[p.provider, p.reference].filter(Boolean).join(" · ")}</span></span>
                    {fundedBy.get(p.item_id) && <Pill label={`funded · ${fundedBy.get(p.item_id).lender}`} tone="pending" small />}
                    <span className={`font-mono text-[12px] ${p.overdue ? "text-danger" : "text-warn"}`}>{p.overdue ? "expired " : "expires "}{fmtDate(p.expiry_date)}</span>
                    {p.premium_estimate != null && <span className="font-mono text-[12px] text-text-muted w-24 text-right">~{money0(Number(p.premium_estimate))}</span>}
                  </div>
                ))}
                {noDate.map((p: any) => (
                  <div key={p.item_id} className="px-4 py-2.5 flex items-center gap-3 text-[13px] text-text-muted">
                    <span className="w-2 h-2 rounded-full flex-shrink-0 bg-text-dim" />
                    <span className="flex-1 min-w-0 truncate">{p.display_name}<span> · {[p.provider, p.reference].filter(Boolean).join(" · ")}</span></span>
                    <span className="font-mono text-[12px]">no expiry date on file</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Per policy — the reason this board exists */}
          <Card className="mb-4">
            <div className="p-4 border-b border-line flex items-center justify-between gap-2 flex-wrap">
              <div>
                <div className="font-semibold text-[14px]">By policy</div>
                <div className="text-[11px] text-text-muted mt-0.5">Every policy on the program, with what it cost this financial year and last, ex-GST. "Not against a policy" is every insurance bill nobody has put a policy on — pick the policy on the coding card and it moves.</div>
              </div>
              {policyFilter !== "all" && <Button variant="secondary" size="sm" onClick={() => setPolicyFilter("all")}>Show all</Button>}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] min-w-[820px]">
                <thead><tr className="border-b border-line">
                  {["Policy", "Category", "Provider", "Expires", "Settled", fyLabel(thisFy), fyLabel(lastFy), "Estimate"].map((h, i) => (
                    <th key={h} className={`px-4 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted whitespace-nowrap ${i >= 5 ? "text-right" : "text-left"} ${i === 0 ? "sticky left-0 bg-ink-raised z-10" : ""}`}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {perPolicy.map((p, i) => (
                    <tr key={p.key} onClick={() => setPolicyFilter(policyFilter === p.key ? "all" : p.key)}
                        className={`border-b border-line last:border-b-0 cursor-pointer hover:bg-ink-hover ${policyFilter === p.key ? "bg-ink-hover" : i % 2 ? "bg-ink/40" : ""} ${p.key === NO_POLICY ? "text-text-muted" : ""}`}>
                      <td className={`px-4 py-2 sticky left-0 z-10 ${policyFilter === p.key ? "bg-ink-hover" : "bg-ink-raised"}`}>
                        {p.key === NO_POLICY ? <span className="italic">{p.name}</span> : <span className="block leading-tight"><span className="block font-medium">{p.name}</span>{p.reference && <span className="block text-[11px] text-text-muted font-mono">{p.reference}</span>}</span>}
                      </td>
                      <td className="px-4 py-2 text-[12px]">{p.category ?? "—"}</td>
                      <td className="px-4 py-2 text-[12px]">{p.provider ?? "—"}</td>
                      <td className="px-4 py-2 font-mono text-[12px]">{p.expiry ? <span className={p.expiry < todayK ? "text-danger" : p.expiry <= in90 ? "text-warn" : "text-text-muted"}>{fmtDate(p.expiry)}</span> : <span className="text-text-dim">—</span>}</td>
                      <td className="px-4 py-2 text-[12px]">
                        {p.funded ? <Pill label={`${p.funded.lender} funds it`} tone="pending" small /> : p.settledByLoan ? <Pill label="by a loan" tone="pending" small /> : p.key === NO_POLICY ? "" : <span className="text-text-dim">bank</span>}
                      </td>
                      <td className="px-4 py-2 font-mono text-right font-semibold">{p.fy ? money0(p.fy) : <span className="text-text-dim">—</span>}</td>
                      <td className="px-4 py-2 font-mono text-right text-text-muted">{p.last ? money0(p.last) : <span className="text-text-dim">—</span>}</td>
                      <td className="px-4 py-2 font-mono text-right text-text-muted">{p.estimate != null ? `~${money0(p.estimate)}` : <span className="text-text-dim">—</span>}</td>
                    </tr>
                  ))}
                  {perPolicy.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-[12px] text-text-muted italic">No policies on the program and no insurance bills yet.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-line text-[11px] text-text-dim">
              "Funds it" = a premium-funding loan is linked to the policy on Commercial › Loans. Estimate is the premium recorded on the program, for a sanity check against the bills.
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
            <Card><div className="p-4">
              <div className="text-[13px] font-medium">{fyLabel(thisFy)} by Xero account</div>
              <div className="text-[11px] text-text-muted mb-1">{fmtMoney(totals.fy)} ex-GST so far</div>
              {byLineWithSpend.length === 0 ? <div className="py-10 text-center text-[12px] text-text-muted italic">Nothing billed to the insurance accounts this financial year yet.</div> : (
                <div style={{ width: "100%", height: 190 }}><ResponsiveContainer><PieChart>
                  <Pie data={byLineWithSpend} dataKey="value" nameKey="name" innerRadius={52} outerRadius={78} paddingAngle={1}>
                    {byLineWithSpend.map((c, i) => <Cell key={c.code} fill={colourFor(c.code, i)} />)}
                  </Pie>
                  <RTooltip formatter={(v: any) => fmtMoney(Number(v))} />
                </PieChart></ResponsiveContainer></div>
              )}
            </div></Card>
            <Card className="lg:col-span-2"><div className="p-4">
              <div className="text-[13px] font-medium mb-2">Where it goes</div>
              <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
                {byLine.map((c, i) => (
                  <button key={c.code} type="button" onClick={() => setLineFilter(lineFilter === c.code ? "all" : c.code)}
                          className={`w-full flex items-center gap-2 text-[12px] rounded px-1 -mx-1 py-1 hover:bg-ink-hover ${lineFilter === c.code ? "bg-ink-hover" : ""}`}>
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: colourFor(c.code, i) }} />
                    <span className="text-text flex-1 truncate text-left">{c.name}</span>
                    <span className="font-mono text-text-dim w-14 text-right">{c.code === "uncoded" ? "—" : c.code}</span>
                    <span className="font-mono text-text-muted w-10 text-right">{totals.fy > 0 ? Math.round((c.value / totals.fy) * 100) : 0}%</span>
                    <span className="font-mono text-text w-24 text-right">{fmtMoney(c.value)}</span>
                  </button>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-line text-[11px] text-text-dim flex items-start gap-1.5">
                <Info size={12} className="mt-0.5 shrink-0"/>
                <span>Click a line to filter the bills below. The accounts listed come from Finance settings (insurance_xero_accounts); a bill that names a policy or carries OX06_INS shows whatever account it sits on. {totals.unassigned > 0 && <>{fmtMoney(totals.unassigned)} this year has no policy picked yet.</>}</span>
              </div>
            </div></Card>
          </div>

          <Card>
            <div className="p-4 border-b border-line flex items-center justify-between gap-2 flex-wrap">
              <div className="font-semibold text-[14px]">
                Insurance bills
                {lineFilter !== "all" && <span className="text-text-muted font-normal"> — {nameFor(lineFilter)}</span>}
                {policyFilter !== "all" && <span className="text-text-muted font-normal"> — {perPolicy.find(p => p.key === policyFilter)?.name ?? ""}</span>}
              </div>
              {(lineFilter !== "all" || policyFilter !== "all") && <Button variant="secondary" size="sm" onClick={() => { setLineFilter("all"); setPolicyFilter("all"); }}>Show all</Button>}
            </div>
            {bills.length === 0 ? <EmptyState title="No insurance bills" description="Bills on an insurance Xero account, naming a policy, or coded OX06_INS appear here." icon={Receipt} /> : (
              <div className="overflow-x-auto"><table className="w-full text-[13px]">
                <thead><tr className="border-b border-line">
                  {["Date", "FY", "Policy", "Xero account", "Supplier", "Invoice #", "Ex-GST", "Inc-GST", "Settled", "Status"].map(h => <th key={h} className="text-left px-4 py-2 text-[10px] font-medium uppercase tracking-wider2 text-text-muted">{h}</th>)}
                </tr></thead>
                <tbody>
                  {bills.map((b: any, i: number) => (
                    <tr key={b.ap_invoice_id} className={`border-b border-line last:border-b-0 ${i % 2 ? "bg-ink/40" : ""}`}>
                      <td className="px-4 py-2 font-mono text-[12px] text-text-muted">{fmtDate(b.spend_date)}</td>
                      <td className="px-4 py-2 font-mono text-[11px] text-text-dim">{fyLabel(b.fy_ending)}</td>
                      <td className="px-4 py-2 text-[12px] max-w-[260px]">
                        {(() => {
                          const sug = b.policy_item_id ? null : suggestFor(b);
                          return (
                            <select value={b.policy_item_id ?? ""} disabled={savingBill === b.ap_invoice_id}
                              onChange={e => void setBillPolicy(b.ap_invoice_id, e.target.value)}
                              title="Which policy is this bill for? Saves straight to the bill."
                              className={`bg-ink border rounded px-1.5 py-0.5 text-[12px] text-text max-w-full ${b.policy_item_id ? "border-line" : sug ? "border-warn/70" : "border-accent/50"}`}>
                              <option value="">{sug ? "— pick (suggested below) —" : "— pick a policy —"}</option>
                              {sug && <option value={sug}>★ {policies.find((p: any) => p.item_id === sug)?.display_name} — looks like this bill</option>}
                              {policies.filter((p: any) => p.item_id !== sug).map((p: any) => (
                                <option key={p.item_id} value={p.item_id}>{p.display_name}{p.provider ? ` · ${p.provider}` : ""}</option>
                              ))}
                            </select>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-2 text-[12px]">{b.xero_account_name}{b.xero_account_code && <span className="font-mono text-[11px] text-text-dim ml-1.5">{b.xero_account_code}</span>}</td>
                      <td className="px-4 py-2 text-[12px]">{b.supplier_name ?? "—"}</td>
                      <td className="px-4 py-2 font-mono text-[11px] text-text-muted">{b.invoice_number ?? "—"}</td>
                      <td className="px-4 py-2 font-mono">{fmtMoney(b.amount_ex_gst)}</td>
                      <td className="px-4 py-2 font-mono text-text-muted">{fmtMoney(b.amount_inc_gst)}</td>
                      <td className="px-4 py-2 text-[12px]">{b.settled_by === "loan" ? <Pill label={b.settled_by_lender ? `${b.settled_by_lender} pays` : "by a loan"} tone="pending" small /> : <span className="text-text-dim">bank</span>}</td>
                      <td className="px-4 py-2"><Pill label={String(b.status).replace(/_/g, " ")} tone={b.status === "paid" ? "approved" : b.status === "disputed" ? "rejected" : "pending"} small /></td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
